import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Nadpis sekcie karty nálezu: čo to je a jednou vetou prečo. Ten istý tvar
 * pri všetkých sekciách, aby sa dali čítať zhora nadol ako zoznam.
 */
export const SectionHeading: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
}> = ({ title, description }) => (
  <Stack gap={0.5} sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
    {description && (
      <Typography
        sx={{ fontSize: 13, color: 'text.secondary', textWrap: 'pretty' }}
      >
        {description}
      </Typography>
    )}
  </Stack>
);

type Props = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Ovládanie vpravo od nadpisu - prepínač, výber, tlačidlo. */
  control?: React.ReactNode;
  /** Obsah pod nadpisom. */
  children?: React.ReactNode;
  /** Obsah bez vnútorného okraja - riadky zoznamu cez celú šírku karty. */
  flush?: boolean;
  /** Sivá pätička s akciou karty. */
  footer?: React.ReactNode;
  /** Červený rám - akcia, ktorá niečo berie (odstránenie z kategórie). */
  tone?: 'danger';
};

/**
 * Sekcia karty nálezu ako samostatná biela karta na sivom podklade (návrh
 * „Karta nálezu" 1a): čo to je vľavo, ovládanie vpravo, akcia v pätičke.
 *
 * Kým boli sekcie len kroky oddelené tichou linkou, splývali - riešiteľ,
 * rozhodnutie, história aj odstránenie vyzerali ako jeden dlhý formulár
 * a nebolo vidieť, čo k čomu patrí a ktoré tlačidlo čo uloží. Kreslí sa len
 * na desktope; mobilná obrazovka nálezu ostáva postupnosťou krokov, na 390 px
 * by karty v karte zjedli šírku.
 */
const IssueSection: React.FC<Props> = ({
  title,
  description,
  control,
  children,
  flush = false,
  footer,
  tone,
}) => {
  const hasHeading = !!title || !!control;

  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: (theme) =>
          `1px solid ${
            tone === 'danger'
              ? theme.palette.primary.light
              : theme.palette.divider
          }`,
      }}
    >
      {hasHeading && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: control ? 'minmax(0, 1fr) auto' : '1fr',
            alignItems: 'center',
            gap: 3,
            px: 2.5,
            pt: 2.5,
            pb: children ? 1.5 : 2.5,
          }}
        >
          {title ? (
            <SectionHeading title={title} description={description} />
          ) : (
            <span />
          )}
          {control}
        </Box>
      )}

      {children && (
        <Box
          sx={
            flush ? undefined : { px: 2.5, pb: 2.5, pt: hasHeading ? 0 : 2.5 }
          }
        >
          {children}
        </Box>
      )}

      {footer && (
        <Stack
          direction='row'
          alignItems='center'
          gap={2}
          flexWrap='wrap'
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: 'grey.50',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {footer}
        </Stack>
      )}
    </Box>
  );
};

export default IssueSection;
